import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Textarea } from '~/components/ui/textarea';
import { useUpdateSyncProduct } from '~/features/explorer/hooks/use-products-catalog';
import { parseProductJson, prettifyJson } from '~/features/explorer/utils/products-catalog';
import { cn } from '~/utils/cn';

type Product = Record<string, unknown>;

interface ProductEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  connectionId: string;
  subsidiaryId?: string | undefined;
}

/**
 * Raw-JSON product editor. Prefills a Textarea with the prettified product,
 * validates the edited JSON on save, and pushes the update through the
 * subsidiary-scoped update mutation (closing on success).
 */
export function ProductEditDialog({
  open,
  onOpenChange,
  product,
  connectionId,
  subsidiaryId,
}: ProductEditDialogProps) {
  const { t } = useTranslation('explorer');
  const [json, setJson] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const updateMutation = useUpdateSyncProduct(subsidiaryId);

  // Re-seed the editor whenever a new product opens — render-time compare
  // avoids the set-state-in-effect anti-pattern.
  const [lastProduct, setLastProduct] = useState(product);
  if (product !== lastProduct) {
    setLastProduct(product);
    setJson(product ? prettifyJson(product) : '');
    setParseError(null);
  }

  function handleSave() {
    const parsed = parseProductJson(json);
    if (!parsed) {
      setParseError(t('products.invalidJson'));
      return;
    }
    setParseError(null);
    updateMutation.mutate(
      { connectionId, product: parsed },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>{t('products.editTitle')}</DialogTitle>
          <DialogDescription>{t('products.editDescription')}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1">
          <Textarea
            value={json}
            onChange={(e) => {
              setJson(e.target.value);
              if (parseError) setParseError(null);
            }}
            disabled={updateMutation.isPending}
            spellCheck={false}
            className="h-80 w-full resize-y font-mono text-sm"
            aria-label={t('products.jsonEditorLabel')}
          />
          {parseError && <p className="text-destructive mt-1.5 text-sm">{parseError}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className={cn(updateMutation.isPending ? 'cursor-not-allowed' : 'cursor-pointer')}
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={updateMutation.isPending}
          >
            {t('products.cancel')}
          </Button>
          <Button
            className={cn(updateMutation.isPending ? 'cursor-not-allowed' : 'cursor-pointer')}
            onClick={handleSave}
            isLoading={updateMutation.isPending}
          >
            {t('products.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
